import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { Tables } from '@/integrations/supabase/types';
import { Staff } from './SchedulerTypes';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { useClinic } from '@/contexts/ClinicContext';
import { useToast } from '@/hooks/use-toast';

type Project = Tables<'projects'>;
type ProjectStaff = Tables<'project_staff'>;

interface ProjectWithDetails extends Project {
  projectStaff?: ProjectStaff[];
}

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (project: Omit<Project, 'id' | 'created_at' | 'updated_at'>, staffIds: string[]) => void;
  staff: Staff[];
  project?: ProjectWithDetails | null;
  selectedClinic?: string;
}

const ProjectModal: React.FC<ProjectModalProps> = ({
  isOpen,
  onClose,
  onSave,
  staff,
  project,
  selectedClinic,
}) => {
  const { currentClinic, clinics } = useClinic();
  const currentClinicId = selectedClinic || currentClinic?.id || null;
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [clinicId, setClinicId] = useState('');
  const [pmStaffId, setPmStaffId] = useState('');
  const [status, setStatus] = useState<'active' | 'on-hold' | 'completed' | 'cancelled'>('active');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [showStaffDropdown, setShowStaffDropdown] = useState(false);

  useEffect(() => {
    if (project) {
      console.log('Editing project:', project);
      setName(project.name);
      setDescription(project.description || '');
      
      // When editing: preserve the project's clinic_id unless we're in a specific clinic context
      let editClinicId = project.clinic_id;
      
      // Only override with current clinic if we're in a specific clinic view (not 'all')
      if (currentClinicId && currentClinicId !== 'all') {
        editClinicId = currentClinicId;
      }
      
      // Fallback to first clinic if no clinic_id exists (shouldn't happen but safety check)
      if (!editClinicId) {
        console.warn('Project missing clinic_id, using first available clinic:', project);
        editClinicId = clinics[0]?.id || '';
      }
      
      setClinicId(editClinicId);
      console.log('Final clinic ID for edit:', editClinicId, { 
        projectClinicId: project.clinic_id, 
        currentClinicId, 
        firstClinic: clinics[0]?.id 
      });
      
      setPmStaffId(project.pm_staff_id || '');
      setStatus((project.status as 'active' | 'on-hold' | 'completed' | 'cancelled') || 'active');
      setStartDate(project.start_date ? new Date(project.start_date) : undefined);
      setEndDate(project.end_date ? new Date(project.end_date) : undefined);
      
      // Get staff IDs from projectStaff relationship
      const staffIds = project.projectStaff?.map(ps => ps.staff_id) || [];
      setSelectedStaffIds(staffIds);
    } else {
      // Reset form for new project
      setName('');
      setDescription('');
      
      // For new project: use current clinic if specific, otherwise first clinic
      const newClinicId = currentClinicId && currentClinicId !== 'all' ? currentClinicId : (clinics[0]?.id || '');
      setClinicId(newClinicId);
      console.log('Setting clinic ID for new project:', newClinicId, { currentClinicId, firstClinic: clinics[0]?.id });
      
      setPmStaffId('');
      setStatus('active');
      setStartDate(undefined);
      setEndDate(undefined);
      setSelectedStaffIds([]);
    }
  }, [project, currentClinicId, clinics]);

  const handleSave = () => {
    console.log('handleSave called', { name, clinicId, currentClinicId, projectClinicId: project?.clinic_id });
    if (!name.trim()) {
      console.log('Validation failed: name is empty');
      return;
    }

    // Determine the final clinic ID:
    // 1. If in a specific clinic context (not 'all'), use that
    // 2. Otherwise, use the clinicId from state (which should be set from the form or existing project)
    const finalClinicId = currentClinicId && currentClinicId !== 'all' ? currentClinicId : clinicId;
    
    if (!finalClinicId) {
      console.log('Validation failed: no clinic ID', { currentClinicId, clinicId, finalClinicId, project });
      toast({
        title: 'Clinic Required',
        description: 'Please select a clinic for this project.',
        variant: 'destructive',
      });
      return;
    }
    
    if (!pmStaffId) {
      console.log('Validation failed: no PM selected');
      toast({
        title: 'Project Manager Required',
        description: 'Please select a Project Manager for this project.',
        variant: 'destructive',
      });
      return;
    }
    
    console.log('Final clinic ID:', finalClinicId);

    // Deduplicate staff IDs to prevent constraint violations
    // The PM might already be in selectedStaffIds from the auto-add logic
    const uniqueStaffIds = [...new Set([...selectedStaffIds, pmStaffId])];
    
    onSave({
      name: name.trim(),
      description: description.trim() || null,
      clinic_id: finalClinicId,
      pm_staff_id: pmStaffId,
      status,
      start_date: startDate ? format(startDate, 'yyyy-MM-dd') : null,
      end_date: endDate ? format(endDate, 'yyyy-MM-dd') : null,
    }, uniqueStaffIds); // Pass deduplicated staff list
  };

  const toggleStaffSelection = (staffId: string) => {
    if (staffId === pmStaffId) return; // Can't deselect PM
    
    setSelectedStaffIds(prev => {
      if (prev.includes(staffId)) {
        return prev.filter(id => id !== staffId);
      } else {
        return [...prev, staffId];
      }
    });
  };

  const removeStaff = (staffId: string) => {
    if (staffId === pmStaffId) return; // Can't remove PM
    setSelectedStaffIds(prev => prev.filter(id => id !== staffId));
  };

  const selectedStaffNames = selectedStaffIds
    .map(id => staff?.find(s => s.id === id)?.name)
    .filter(Boolean);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{project ? 'Edit Project' : 'Create New Project'}</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4">
          <div className="grid gap-4 py-4">
            {/* Project Name */}
            <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
              <Label htmlFor="name" className="text-left sm:text-right">
                Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full sm:col-span-3"
                placeholder="Enter project name"
              />
            </div>

            {/* Description */}
            <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:gap-4">
              <Label htmlFor="description" className="text-left sm:text-right mt-2">
                Description
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full sm:col-span-3"
                placeholder="Enter project description (optional)"
                rows={3}
              />
            </div>

            {/* Clinic - Show when in 'all' mode or when editing a project without a valid clinic */}
            {(currentClinicId === 'all' || !currentClinicId || (project && !project.clinic_id)) && (
              <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
                <Label htmlFor="clinic" className="text-left sm:text-right">
                  Clinic <span className="text-red-500">*</span>
                </Label>
                <Select value={clinicId} onValueChange={setClinicId}>
                  <SelectTrigger className="w-full sm:col-span-3">
                    <SelectValue placeholder="Select clinic (required)" />
                  </SelectTrigger>
                  <SelectContent>
                    {clinics.map((clinic) => (
                      <SelectItem key={clinic.id} value={clinic.id}>
                        {clinic.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Project Manager */}
            <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
              <Label htmlFor="pm" className="text-left sm:text-right">
                Project Manager <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={pmStaffId} 
                onValueChange={(value) => {
                  setPmStaffId(value);
                  // Auto-add PM to selected staff
                  if (!selectedStaffIds.includes(value)) {
                    setSelectedStaffIds(prev => [...prev, value]);
                  }
                }}
              >
                <SelectTrigger className="w-full sm:col-span-3">
                  <SelectValue placeholder="Select project manager (required)" />
                </SelectTrigger>
                <SelectContent>
                  {staff?.map((staffMember) => (
                    <SelectItem key={staffMember.id} value={staffMember.id}>
                      {staffMember.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Project Team */}
            <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:gap-4">
              <Label className="text-left sm:text-right mt-2">
                Project Team
              </Label>
              <div className="w-full sm:col-span-3 space-y-2">
                <div className="relative">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setShowStaffDropdown(!showStaffDropdown)}
                  >
                    {selectedStaffIds.length === 0 
                      ? "Select team members" 
                      : `${selectedStaffIds.length} members selected`}
                  </Button>
                  
                  {showStaffDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg">
                      <ScrollArea className="h-48 p-2">
                        {staff?.map((staffMember) => (
                          <div
                            key={staffMember.id}
                            className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded"
                          >
                            <Checkbox
                              checked={selectedStaffIds.includes(staffMember.id) || staffMember.id === pmStaffId}
                              onCheckedChange={() => toggleStaffSelection(staffMember.id)}
                              disabled={staffMember.id === pmStaffId}
                            />
                            <Label className="flex-1 cursor-pointer">
                              {staffMember.name}
                              {staffMember.id === pmStaffId && (
                                <Badge variant="secondary" className="ml-2 text-xs">PM</Badge>
                              )}
                            </Label>
                          </div>
                        ))}
                      </ScrollArea>
                    </div>
                  )}
                </div>

                {/* Selected Staff Display */}
                {selectedStaffIds.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {selectedStaffIds.map(staffId => {
                      const staffMember = staff?.find(s => s.id === staffId);
                      if (!staffMember) return null;
                      
                      return (
                        <Badge 
                          key={staffId} 
                          variant={staffId === pmStaffId ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {staffMember.name}
                          {staffId === pmStaffId && " (PM)"}
                          {staffId !== pmStaffId && (
                            <button
                              onClick={() => removeStaff(staffId)}
                              className="ml-1 hover:text-red-500"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
              <Label htmlFor="status" className="text-left sm:text-right">
                Status
              </Label>
              <Select value={status} onValueChange={(value) => setStatus(value as 'active' | 'on-hold' | 'completed' | 'cancelled')}>
                <SelectTrigger className="w-full sm:col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on-hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
              <Label htmlFor="startDate" className="text-left sm:text-right">
                Start Date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full sm:col-span-3 justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date */}
            <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
              <Label htmlFor="endDate" className="text-left sm:text-right">
                End Date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full sm:col-span-3 justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    disabled={(date) => startDate ? date < startDate : false}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!name.trim()}
          >
            {project ? 'Update' : 'Create'} Project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectModal;